
import { computed, defineComponent, reactive } from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import ServicoContaContabilReferenciada from '@/servicos/Cadastros/PlanosContas/ServicoContaContabilReferenciada';

export default defineComponent({
  name: 'SelecionarContaContabilReferenciada',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    apresentarNumeroConta: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'update:descricaoSelecionado'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();

    let debounce = 0;
    const servicoContaContabilReferenciada = new ServicoContaContabilReferenciada();

    const state = reactive({
      listaContasContabeis: [] as IOption[],
      fetching: false,
      emDigitacao: false,
    });

    async function obterContaContabilPorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }
      if (!(valor > 0)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === 0) {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaContasContabeis)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaContasContabeis.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaContasContabeis.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }
      }

      if (!efetuarConsulta) return;

      state.listaContasContabeis = [];

      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        apenasAtivos: false, recursoAssociado: '',
      };

      if (props.varios) {
        parametrosConsultaRapida.filtrarPorVariosCodigos = true;
        parametrosConsultaRapida.valores = valor;
      } else {
        parametrosConsultaRapida.filtrarPorCodigo = true;
        parametrosConsultaRapida.valor = valor;
      }
      const listaContasContabeis = await servicoContaContabilReferenciada.consultaRapida(parametrosConsultaRapida);
      state.listaContasContabeis = montaOpcoesComListaConsultaRapida(listaContasContabeis);
    }

    async function pesquisarContaContabil(valorPesquisa: any) {
      state.emDigitacao = true;

      if (!UtilitarioGeral.valorValido(valorPesquisa)) { return; }

      state.listaContasContabeis = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '',
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }

        const listaContasContabeis = await servicoContaContabilReferenciada.consultaRapida(parametrosConsultaRapida);
        state.listaContasContabeis = montaOpcoesComListaConsultaRapida(listaContasContabeis);
      }, 600);
    }

    function obterContaContabil():any {
      if (props.codigoSelecionado === 0) {
        return undefined;
      }
      obterContaContabilPorCodigo(props.codigoSelecionado);
      return props.codigoSelecionado;
    }

    const computedDescricaoSelecionado = computed({
      get: () => props.descricaoSelecionado,
      set: (val: string) => {
        emit('update:descricaoSelecionado', val);
      },
    });

    const computedCodigoSelecionado = computed({
      get: () => obterContaContabil(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
        const option = state.listaContasContabeis.find((c) => c.value === val);
        if (option?.label !== undefined && option?.label !== '') {
          computedDescricaoSelecionado.value = option?.label;
        }
      },
    });

    function obterContasContabeis():number[] {
      obterContaContabilPorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterContasContabeis(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    return {
      props,
      state,
      pesquisarContaContabil,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
    };
  },
});
